import type { BaseRoleRepository } from '../../domain/repositories/BaseRoleRepository'
import { BaseRoleModel } from '../models/BaseRoleModel'
import type { UpdateBaseRoleEntity } from '../../domain/entities/UpdateBaseRoleEntity'
import { UpdateBaseRoleModel } from '../models/updateBaseRoleModel'
import { BaseRolesService } from '@/backend/market'

/**
 * Implementation of the BaseRoleRepository interface.
 * This class provides methods for interacting with the backend service for managing base roles.
 */
export class BaseRoleRepositoryImpl implements BaseRoleRepository {
  /**
   * Fetches all base roles from the backend service.
   *
   * @returns A promise that resolves to an array of BaseRoleModel instances or 'error' if an error occurs.
   */
  async getBaseRoles(): Promise<BaseRoleModel[] | { error: true }> {
    try {
      const response = await BaseRolesService.getBaseRolesAdmin()
      const baseRoleModels = response.baseRoles?.map((res) => new BaseRoleModel(res)) ?? []
      return baseRoleModels
    } catch {
      return { error: true }
    }
  }

  /**
   * Updates a specific base role in the backend service.
   *
   * @param id - The unique identifier of the base role to update.
   * @param data - The updated data for the base role.
   *
   * @returns A promise that resolves to an object with a boolean property 'isSuccess' indicating whether the update was successful.
   */
  async updateBaseRole(id: number, data: UpdateBaseRoleEntity): Promise<{ isSuccess: boolean }> {
    const updateBaseRoleModel = new UpdateBaseRoleModel(data)
    try {
      await BaseRolesService.putBaseRoleAdmin({
        id,
        requestBody: updateBaseRoleModel
      })

      return { isSuccess: true }
    } catch {
      return { isSuccess: false }
    }
  }
}
